import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { ApiError } from '../../api.js';
import { db } from '../../db/local.js';
import {
  isUnlocked,
  readJournalLock,
  subscribeUnlock,
  unlockedSnapshot,
  unlockWithFaceId,
  unlockWithPassword,
} from '../../journalLock.js';
import type { Authenticate, UnlockResult } from '../../journalLock.js';
import { PasswordInput } from '../PasswordInput.js';

/**
 * Legt die Sperre über das Tagebuch, wenn sie auf diesem Gerät an ist.
 *
 * Der Inhalt bleibt dabei eingehängt (nur `hidden`) — sperrt die App beim
 * Wechsel in den Hintergrund, ist ein halb geschriebener Gedanke danach noch
 * da. Sichtbar ist er erst wieder nach dem Entsperren.
 */
export function JournalLockGate({
  children,
  authenticate,
}: {
  children: ReactNode;
  /** Für Tests: das Gerät nachstellen. */
  authenticate?: Authenticate | undefined;
}): React.JSX.Element {
  const enabled = useLiveQuery(() => readJournalLock(db), [], null);
  const unlockedAt = useSyncExternalStore(subscribeUnlock, unlockedSnapshot);
  // Beim Öffnen gilt die Frist von fünf Minuten. Einmal offen, bleibt es offen,
  // bis die App in den Hintergrund geht — keine Sperre mitten im Schreiben.
  const [atOpen] = useState(() => ({ stale: !isUnlocked(), unlockedAt: unlockedSnapshot() }));
  const locked =
    enabled === true && (unlockedAt === null || (atOpen.stale && unlockedAt === atOpen.unlockedAt));

  if (enabled === null) return <></>;
  return (
    <>
      {locked ? <LockScreen authenticate={authenticate} /> : null}
      <div hidden={locked}>{children}</div>
    </>
  );
}

function LockScreen({
  authenticate,
}: {
  authenticate?: Authenticate | undefined;
}): React.JSX.Element {
  const { t } = useTranslation();
  const passwordId = useId();
  const [result, setResult] = useState<UnlockResult | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => button.current?.focus(), []);

  async function faceId() {
    setBusy(true);
    setResult(await unlockWithFaceId(authenticate));
    setBusy(false);
  }

  return (
    <section aria-labelledby="journal-lock-heading" className="kw-tile flex flex-col gap-3">
      <h2 id="journal-lock-heading" className="font-display text-xl font-bold">
        {t('journalLock.locked')}
      </h2>
      <p className="text-sm text-ink-muted">{t('journalLock.lockedHint')}</p>
      <button
        ref={button}
        type="button"
        disabled={busy}
        onClick={() => void faceId()}
        className="kw-btn"
      >
        {t('journalLock.unlock')}
      </button>
      {result && result !== 'unlocked' ? (
        <p role="status" className="text-sm text-warn">
          {t(`journalLock.result.${result}`)}
        </p>
      ) : null}
      {usePassword ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            setBusy(true);
            unlockWithPassword(password)
              .catch((cause: unknown) => {
                const status = cause instanceof ApiError ? cause.status : 0;
                setError(
                  t(
                    status === 403
                      ? 'journalLock.wrongPassword'
                      : status === 429
                        ? 'journalLock.tooMany'
                        : 'journalLock.offline',
                  ),
                );
              })
              .finally(() => setBusy(false));
          }}
        >
          <label htmlFor={passwordId} className="kw-label">
            {t('journalLock.password')}
          </label>
          <PasswordInput
            id={passwordId}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={busy || !password}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('journalLock.unlockPassword')}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setUsePassword(true)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('journalLock.usePassword')}
        </button>
      )}
    </section>
  );
}
